import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import LinkRow from "./LinkRow";
import RichTextToolbar, { type InlineFormatT, type ListFormatT } from "./RichTextToolbar";
import { formatsFor, isAllowedLinkHref, normalizeLinkUrl } from "./helpers";
import type { RichTextEditorPropsT } from "./types";
import * as S from "./styles";

type QuillT = ReturnType<ReactQuill["getEditor"]>;
type RangeT = NonNullable<ReturnType<QuillT["getSelection"]>>;

const Delta = Quill.import("delta");

const PASTE_PATCHED = "__pasteMatchersInstalled";

const unwrapNormalWeightBold = (container: Element) => {
  Array.from(container.querySelectorAll("b"))
    .filter((bold) => bold.style.fontWeight === "normal")
    .forEach((bold) => bold.replaceWith(...Array.from(bold.childNodes)));
};

const installPasteMatchers = (quill: QuillT) => {
  const { clipboard } = quill;
  if ((clipboard as unknown as Record<string, unknown>)[PASTE_PATCHED]) return;
  (clipboard as unknown as Record<string, unknown>)[PASTE_PATCHED] = true;

  const convert = clipboard.convert.bind(clipboard);
  const { container } = clipboard as unknown as { container: Element };
  clipboard.convert = ((html?: string) => {
    if (html === undefined) unwrapNormalWeightBold(container);
    return convert(html);
  }) as typeof clipboard.convert;

  clipboard.addMatcher("a", (node, delta) => {
    if (isAllowedLinkHref(node.getAttribute("href") ?? "")) return delta;
    return (delta.ops ?? []).reduce((unlinked, op) => {
      const { link: _link, ...attributes } = op.attributes ?? {};
      return unlinked.insert(op.insert, Object.keys(attributes).length > 0 ? attributes : undefined);
    }, new Delta());
  });
};

const QUILL_MODULES = { toolbar: false, clipboard: { matchVisual: false } };

const RichTextEditor = ({
  value,
  onChange,
  tools,
  ariaLabel,
  placeholder,
  onBlur,
  error,
  helperText,
  id,
}: RichTextEditorPropsT) => {
  const quillRef = useRef<ReactQuill>(null);
  const linkRange = useRef<RangeT | null>(null);
  const [activeFormats, setActiveFormats] = useState<Record<string, unknown>>({});
  const [linkText, setLinkText] = useState<string | null>(null);
  const helperId = useId();
  const toolKey = tools.join(",");
  const formats = useMemo(() => formatsFor(tools), [toolKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editor = () => quillRef.current?.getEditor();

  useEffect(() => {
    const quill = editor();
    if (!quill) return undefined;

    const { root } = quill;
    root.setAttribute("role", "textbox");
    root.setAttribute("aria-multiline", "true");
    root.setAttribute("aria-label", ariaLabel);
    root.setAttribute("data-placeholder", placeholder ?? "");
    if (error) root.setAttribute("aria-invalid", "true");
    else root.removeAttribute("aria-invalid");
    if (id) root.id = id;
    if (helperText) root.setAttribute("aria-describedby", helperId);
    else root.removeAttribute("aria-describedby");

    const refreshFormats = () => {
      const range = quill.getSelection();
      setActiveFormats(range ? quill.getFormat(range) : {});
    };
    quill.on("editor-change", refreshFormats);
    return () => {
      quill.off("editor-change", refreshFormats);
    };
  }, [ariaLabel, placeholder, id, helperText, helperId, error]);

  useEffect(() => {
    const quill = editor();
    if (quill) installPasteMatchers(quill);
  }, []);

  const toggleInline = (format: InlineFormatT) => {
    const quill = editor();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.format(format, !quill.getFormat(range)[format], "user");
  };

  const toggleList = (list: ListFormatT) => {
    const quill = editor();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.format("list", quill.getFormat(range).list === list ? false : list, "user");
  };

  // Quill's removeFormat mishandles a range that reaches the exact end of the document
  // (it inserts a stray blank line), so stop one character short whenever a range would
  // reach that end. Both clear-formatting paths below route through this.
  const removeFormatClamped = (quill: QuillT, index: number, length: number) => {
    const end = index + length;
    const clampedLength = end >= quill.getLength() ? quill.getLength() - 1 - index : length;
    quill.removeFormat(index, clampedLength, "user");
  };

  const clearFormatting = () => {
    const quill = editor();
    if (!quill) return;
    const range = quill.getSelection(true);
    if (range.length > 0) {
      removeFormatClamped(quill, range.index, range.length);
      return;
    }
    const [line, offset] = quill.getLine(range.index);
    if (!line) return;
    removeFormatClamped(quill, range.index - offset, line.length());
  };

  const openLink = () => {
    const quill = editor();
    if (!quill) return;
    const range = quill.getSelection(true);
    linkRange.current = range;
    setLinkText(quill.getText(range.index, range.length));
  };

  const closeLink = () => {
    linkRange.current = null;
    setLinkText(null);
  };

  const insertLink = (text: string, url: string) => {
    const quill = editor();
    const range = linkRange.current;
    if (!quill || !range) return;

    const href = normalizeLinkUrl(url);
    let end: number;
    if (range.length > 0 && text === quill.getText(range.index, range.length)) {
      quill.formatText(range.index, range.length, "link", href, "user");
      end = range.index + range.length;
    } else {
      const label = text.trim() ? text : href;
      if (range.length > 0) quill.deleteText(range.index, range.length, "user");
      quill.insertText(range.index, label, "link", href, "user");
      end = range.index + label.length;
    }
    quill.setSelection(end, 0, "user");
    quill.format("link", false, "user");
    closeLink();
  };

  return (
    <div>
      <S.EditorFrame error={error}>
        <RichTextToolbar
          tools={tools}
          activeFormats={activeFormats}
          linkOpen={linkText !== null}
          onToggleInline={toggleInline}
          onToggleList={toggleList}
          onOpenLink={openLink}
          onClear={clearFormatting}
        />
        {linkText !== null && <LinkRow initialText={linkText} onInsert={insertLink} onCancel={closeLink} />}
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          modules={QUILL_MODULES}
          formats={formats}
          placeholder={placeholder}
          onBlur={() => onBlur?.()}
          onChange={(html, _delta, _source, quillEditor) =>
            onChange({ html, text: quillEditor.getText().replace(/\n$/, "") })
          }
        />
      </S.EditorFrame>
      {helperText && (
        <S.HelperText id={helperId} error={error}>
          {helperText}
        </S.HelperText>
      )}
    </div>
  );
};

export default RichTextEditor;
